import React, { useContext, useState } from 'react';
import { TextSmallItalics, TextTinyItalics } from '@components/elements/TextStyles';
import { FlexColumn, FlexItem, FlexRow, FlexRowSpaced } from '@components/layout/FlexStyles';
import TextButton from '@components/controls/TextButton';
import SubmissionsStatus from '@components/submissions/SubmissionsStatus';
import styled, { keyframes } from 'styled-components/macro';
import { TextField } from '@mui/material';
import { CreditsContext } from '@stores/CreditsContext';
import { UserContext } from '@stores/UserContext';
import useSubmissions from '@components/submissions/useSubmissions';

const SubmissionCreateForm = ({status, setStatus}) => {
  const { user } = useContext(UserContext);
  const { creditsInfo, setCreditsInfo } = useContext(CreditsContext);
  const { addSubmission } = useSubmissions();

  // ###########################################################################
  // DATA LOADING
  // ###########################################################################
  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState({
    name: '',
    author: user?.regInfo?.name?.last + ', ' + user?.regInfo?.name?.first,
    content: ''
  });

  // ###########################################################################
  // Interactions
  // ###########################################################################

  const handleChange = (event) => {
    let item = event.target.value;
    if (event.target.name.includes('toc')) item = event.target.checked;

    setFields((previous) => ({
      ...previous,
      [event.target.name]: item,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);

    const model = {
      Name: fields.name,
      Author: fields.author,
      Content: fields.content,
    };

    sendForm(model).then();
  };

  const sendForm = async (model) => {
    let resp;

    try {
      resp = await addSubmission(model);

      if (resp.status === 'OK') {
        setStatus({
          status: true,
          documentId: resp.documentId,
          score: resp.score,
          documentHash: resp.documentHash,
        });
        const updatedCredits = {
          creditsAvailable: creditsInfo.creditsAvailable - 1,
          creditsUsed: creditsInfo.creditsUsed + 1,
        };
        setCreditsInfo(updatedCredits);
        resetForm();
      } else if (resp.status === 'Error') {
        setStatus({
          status: false,
          documentId: resp.documentId,
          score: null,
          documentHash: null,
        });
      }
      setIsLoading(false);
    } catch (err) {
      setStatus({
        status: false,
        documentId: null,
        score: null,
        documentHash: null,
      });
      setIsLoading(false);
    }
  };

  const newForm = () => {
    setStatus({
      status: null,
      documentId: null,
      score: null,
      documentHash: null,
    });
    resetForm();
  };

  const resetForm = () => {
    setFields({
      name: '',
      author: '',
      content: ''
    });
  };

  const isFormValid = fields.name !== '' && fields.author !== '' && fields.content !== '';
  return(
    <>
      {status.status === null ? (
        <form>
          {isLoading !== true ? (
            <>
              <FlexColumn>
                <Title>Submit your manuscript's "Materials and Methods" section</Title>
                <FlexItem>
                  <TextTinyItalics>(Fill out submission form content below to submit for analysis)</TextTinyItalics>
                </FlexItem>
              </FlexColumn>
              <FlexRow height={'5px'}/>
              <FlexRow width='100%'>
                <FlexItem width='60%'>
                  <StyledTextField
                    id='submission_name'
                    name='name'
                    type='text'
                    placeholder='Submission Title'
                    label='Title'
                    required
                    fullWidth
                    value={fields.name}
                    onChange={handleChange}
                  />
                </FlexItem>
                <FlexItem width='40%'>
                  <StyledTextField
                    id='author'
                    name='author'
                    type='text'
                    placeholder='Submission Author'
                    label='Author'
                    required
                    fullWidth
                    value={fields.author}
                    onChange={handleChange}
                  />
                </FlexItem>
              </FlexRow>
              <FlexRow height={'5px'}/>
              <FlexRow width='100%'>
                <FlexItem width='100%'>
                  <StyledTextField
                    id='content'
                    name='content'
                    label='Materials and Methods section'
                    placeholder='Content of the manuscripts Materials and Methods section'
                    multiline
                    required
                    fullWidth
                    rows={15}
                    value={fields.content}
                    onChange={handleChange}
                  />
                </FlexItem>
              </FlexRow>
              <FlexRowSpaced width={'100%'}>
                <FlexItem>
                  <TextSmallItalics color={'accent'}>
                    Cost 1 credit per submission
                  </TextSmallItalics>
                </FlexItem>
                <FlexItem>
                  <TextButton
                    label='Submit'
                    isDisabled={!isFormValid}
                    onClick={(e) => handleSubmit(e)}
                  />
                  {/* Do we need this -- no harm?
                  <TextButton
                    label="Cancel"
                    onClick={resetForm}
                  />*/}
                </FlexItem>
              </FlexRowSpaced>
            </>
          ) : (
            <LoadingContainer>
              <Loader />
            </LoadingContainer>
          )}
        </form>
      ) : status.status ? (
        <SubmissionsStatus status={status} newForm={newForm} />
      ) : (
        <div>Error</div>
      )}
    </>
  );
}

export default SubmissionCreateForm;


interface StyledExtraProps {
  small?: boolean;
}

const Title = styled.div`
  display: flex;
  font-size: ${(props) => props.theme.sizes.default};
  color: ${(props) => props.theme.palette.accentPrimary};
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const Loader = styled.div<StyledExtraProps>`
  display: inline-block;
  border-radius: 100%;
  margin: 2px;
  border: ${(p) => p.theme.borders.accentPrimary};
  border-bottom-color: transparent;
  width: ${(props) => (props.small ? 12 : 22)}px;
  height: ${(props) => (props.small ? 12 : 22)}px;
  animation: ${rotate} 0.75s linear infinite;
  background-color: ${(p) => p.theme.palette.backgroundPrimary};
`;


const StyledTextField = styled(TextField)`
  padding-top: 3px;

  width: 100%;
  & .MuiInputBase-root {  // match the border radius around the input field
    border-radius: 10px;
    font-size: 14px;
  }

  & .MuiOutlinedInput-input {  // reduce the padding for more compact form
    padding: 10px;
  }

  & .MuiFormLabel-root {  // set a smaller label (no input) to adjust to reduced padding
    font-size: 14px;
  }

  & .MuiOutlinedInput-input::placeholder {  // adjust the placeholder - when field in focus
    padding: 10px;
    font-size: 12px;
    font-style: italic;
  }
  // Handle the autofill color
  & :-webkit-autofill,
  & :-webkit-autofill:hover,
  & :-webkit-autofill:focus,
  & :-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px ${(p) => p.theme.palette.backgroundPrimary} inset !important;
    -webkit-text-fill-color: ${(p) => p.theme.palette.textPrimary} !important;
  }
  // handle the scroll bar
  & ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  & ::-webkit-scrollbar-track {
    background: ${(p) => p.theme.palette.backgroundSecondary};
  }
  & ::-webkit-scrollbar-thumb {
    background: ${(p) => p.theme.palette.accentPrimary};
    border-radius: 5px;
  }
  & ::-webkit-scrollbar-thumb:hover {
    background: ${(p) => p.theme.palette.accentSecondary};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 80px;
`;
